/**
 * CustomAdSlot Component
 * 
 * A reusable component for displaying custom ads in the UI.
 * Uses IntersectionObserver to track viewability (>=50% visible for 1s).
 * 
 * FEATURE FLAG: Only renders if REACT_APP_ENABLE_CUSTOM_AD_SLOTS === "1"
 * Default is OFF for safety.
 * 
 * RELIABILITY: 
 * - Fails gracefully - returns null on any error
 * - Never blocks core workflows
 * - No error toasts or visible failures
 * 
 * Usage:
 * import { CustomAdSlot } from "../components/CustomAdSlot";
 * import { AD_PLACEMENTS_MAP } from "../constants/adPlacements";
 * 
 * <CustomAdSlot placementKey={AD_PLACEMENTS_MAP.tech_dashboard_sidebar.key} />
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { isPlacementWired } from "../constants/adPlacements";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ENABLE_CUSTOM_AD_SLOTS = process.env.REACT_APP_ENABLE_CUSTOM_AD_SLOTS === "1";

// Generate or retrieve session ID for frequency capping
const getSessionId = () => {
  try {
    let sessionId = sessionStorage.getItem("custom_ad_session_id");
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem("custom_ad_session_id", sessionId);
    }
    return sessionId;
  } catch {
    // sessionStorage might be blocked
    return `fallback-${Date.now()}`;
  }
};

export function CustomAdSlot({ placementKey, className = "", variant = "default" }) {
  const [adData, setAdData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impressionLogged, setImpressionLogged] = useState(false);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef(null);
  const visibilityTimerRef = useRef(null);

  // Fetch ad on mount - fail silently on any error
  useEffect(() => {
    // Feature flag check - render nothing if disabled
    if (!ENABLE_CUSTOM_AD_SLOTS) {
      setLoading(false);
      return;
    }
    
    // Validate placement key exists in registry
    if (!placementKey || !isPlacementWired(placementKey)) {
      setLoading(false);
      return;
    }

    const fetchAd = async () => {
      try {
        const sessionId = getSessionId();
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
        
        const response = await fetch(
          `${API}/custom-ads/serve?placement_key=${encodeURIComponent(placementKey)}&session_id=${sessionId}`,
          { 
            credentials: "include",
            signal: controller.signal
          }
        );
        
        clearTimeout(timeoutId);

        if (response.status === 204 || !response.ok) {
          // No ad available or error - fail silently
          setAdData(null);
        } else {
          const data = await response.json();
          setAdData(data);
        }
      } catch (error) {
        // Fail silently - ads should never block UI
        setAdData(null);
        setHasError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAd();
  }, [placementKey]);

  // Track impression using IntersectionObserver
  // Requirement: >=50% visible for at least 1 second
  const logImpression = useCallback(async () => {
    if (!adData || impressionLogged) return;

    try {
      const sessionId = getSessionId();
      await fetch(`${API}/custom-ads/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          event_type: "impression",
          serve_token: adData.serve_token,
          session_id: sessionId,
        }),
      });
      setImpressionLogged(true);
    } catch (error) {
      // Fail silently - don't disrupt user experience
    }
  }, [adData, impressionLogged]);

  useEffect(() => {
    if (!adData || impressionLogged || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            // Start timer - must be visible for 1 second
            if (!visibilityTimerRef.current) {
              visibilityTimerRef.current = setTimeout(() => {
                logImpression();
              }, 1000);
            }
          } else {
            // Clear timer if no longer visible
            if (visibilityTimerRef.current) {
              clearTimeout(visibilityTimerRef.current);
              visibilityTimerRef.current = null;
            }
          }
        });
      },
      {
        threshold: [0.5], // 50% visibility threshold
      }
    );

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
      }
    };
  }, [adData, impressionLogged, logImpression]);

  // Handle click - use redirect endpoint
  const handleClick = () => {
    if (!adData) return;
    
    // Log click via redirect endpoint (server-side tracking)
    const redirectUrl = `${API}/custom-ads/click?t=${encodeURIComponent(adData.serve_token)}`;
    window.open(redirectUrl, "_blank", "noopener,noreferrer");
  };

  // === RENDER NOTHING CONDITIONS ===
  
  // Feature flag OFF
  if (!ENABLE_CUSTOM_AD_SLOTS) {
    return null;
  }

  // Loading or error - don't show anything
  if (loading || hasError) {
    return null;
  }

  // No ad available
  if (!adData || !adData.creative) {
    return null;
  }

  const { creative } = adData;

  // Render based on creative type and variant
  // Updated styling: clean white card to match FSP design
  if (creative.type === "native") {
    return (
      <div
        ref={containerRef}
        className={`custom-ad-slot custom-ad-native cursor-pointer ${className}`}
        onClick={handleClick}
        data-testid={`custom-ad-slot-${placementKey}`}
        data-ad-placement={placementKey}
      >
        <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden hover:shadow-md transition-shadow">
          {creative.image_url && (
            <img
              src={creative.image_url}
              alt={creative.headline || "Sponsored"}
              className="w-full h-48 object-cover"
              loading="lazy"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="p-4">
            {creative.headline && (
              <div className="text-sm font-semibold text-zinc-900">{creative.headline}</div>
            )}
            {creative.body && (
              <div className="mt-1 text-sm text-zinc-500 line-clamp-2">{creative.body}</div>
            )}
            <div className="mt-3 flex items-center justify-between">
              {creative.cta_text && (
                <span className="text-sm font-medium text-orange-600 hover:text-orange-700">
                  {creative.cta_text} →
                </span>
              )}
              <span className="text-xs text-zinc-400">Sponsored</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Image banner (default) - also use white card styling
  return (
    <div
      ref={containerRef}
      className={`custom-ad-slot custom-ad-image cursor-pointer ${className}`}
      onClick={handleClick}
      data-testid={`custom-ad-slot-${placementKey}`}
      data-ad-placement={placementKey}
    >
      <div className="bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden hover:shadow-md transition-shadow">
        {creative.image_url ? (
          <img
            src={creative.image_url}
            alt="Sponsored"
            className="w-full object-cover"
            style={{ maxHeight: variant === "banner" ? "90px" : "200px" }}
            loading="lazy"
            onError={(e) => { e.target.parentElement.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-24 bg-zinc-50 flex items-center justify-center">
            <span className="text-zinc-400 text-sm">Ad</span>
          </div>
        )}
        <div className="px-4 py-2 flex items-center justify-between border-t border-zinc-100">
          {creative.cta_text ? (
            <span className="text-sm font-medium text-orange-600 hover:text-orange-700">
              {creative.cta_text} →
            </span>
          ) : (
            <span className="text-sm font-medium text-orange-600 hover:text-orange-700">
              Learn more →
            </span>
          )}
          <span className="text-xs text-zinc-400">Sponsored</span>
        </div>
      </div>
    </div>
  );
}

export default CustomAdSlot;
